import { LANDING } from '@/constants/testIds';
import {
  Zap,
  Activity,
  Crosshair,
  MoveRight,
  Eye,
  Layers,
  Brain,
  Hand,
} from 'lucide-react';

const components = [
  { icon: Zap, label: 'Reaction', description: 'Speed of response. How fast signal becomes movement.' },
  { icon: Activity, label: 'Signal', description: 'The measurable score. Progress as a readable, trackable line.' },
  { icon: Crosshair, label: 'Focus', description: 'Depth and convergence. The eye\'s ability to lock and hold.' },
  { icon: MoveRight, label: 'Tracking', description: 'Smooth pursuit. Following a moving target without losing clarity.' },
  { icon: Eye, label: 'Peripheral', description: 'Awareness at the edges. Expanding the useful field of view.' },
  { icon: Layers, label: 'Depth', description: 'Spatial processing. Judging distance and position under pressure.' },
  { icon: Brain, label: 'Processing', description: 'Visual-cognitive speed. Making sense of what the eye delivers.' },
  { icon: Hand, label: 'Coordination', description: 'Hand-eye sync. Translating visual data into precise motor output.' },
];

const FeaturesSection = () => {
  return (
    <section
      id="components"
      data-testid={LANDING.featuresSection}
      className="relative py-24 md:py-32"
    >
      {/* Section divider line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-signal-gold/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-16 md:mb-20">
          <p className="font-mono text-signal-gold text-xs tracking-widest uppercase mb-4">
            What we measure
          </p>
          <h2
            data-testid={LANDING.featuresSectionTitle}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-bone tracking-tight"
          >
            8 components, 1 score.
          </h2>
          <p className="font-body text-slate text-base md:text-lg mt-5 max-w-xl">
            A weekly visual performance score, tracked over time — clarity made visible, not claimed.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {components.map((comp, i) => {
            const Icon = comp.icon;
            const isLarge = i < 2;
            return (
              <div
                key={comp.label}
                data-testid={`${LANDING.featureCard}-${comp.label.toLowerCase()}`}
                className={`bg-surface p-8 md:p-10 group relative overflow-hidden transition-all duration-500 hover:bg-[#1c1c1c] ${
                  isLarge ? 'lg:col-span-2' : ''
                }`}
              >
                {/* Corner accent on hover */}
                <div className="absolute top-0 left-0 w-0 h-px bg-signal-gold transition-all duration-500 group-hover:w-12" />
                <div className="absolute top-0 left-0 w-px h-0 bg-signal-gold transition-all duration-500 group-hover:h-12" />

                <div className="w-14 h-14 rounded-full border border-signal-gold/30 flex items-center justify-center mb-6 group-hover:border-signal-gold/60 transition-all duration-500 group-hover:shadow-[0_0_20px_rgba(201,168,118,0.1)]">
                  <Icon size={24} strokeWidth={1.5} className="text-signal-gold" />
                </div>

                <h3 className="font-heading text-lg font-semibold text-bone mb-2">
                  {comp.label}
                </h3>

                <p className="font-body text-slate text-sm leading-relaxed">
                  {comp.description}
                </p>

                {isLarge && (
                  <div className="mt-8 pt-6 border-t border-white/5">
                    <span className="font-mono text-signal-gold text-3xl font-medium">
                      {i === 0 ? '0.18s' : '87.4'}
                    </span>
                    <span className="font-mono text-slate text-xs ml-3 tracking-wider uppercase">
                      {i === 0 ? 'avg response' : 'weekly score'}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
